import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { lookupWord } from "../Services/dictionaryService";
import { NameySays } from "./Mascot";

const DictionaryLookup = ({ word, category, checksLeft = 5, onCheckUsed }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [definition, setDefinition] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cache, setCache] = useState({});
  // A ref, not just the `loading` state: two rapid clicks can both read
  // `loading` as still false (state updates aren't synchronous), both pass
  // the checksLeft check, and both start a fetch for the same word — each
  // success then calls onCheckUsed(), burning two allowance entries for one
  // intended lookup. A ref is set synchronously, before either click's
  // fetchDefinition call can race the other.
  const isFetchingRef = useRef(false);

  // Clean word for API call (remove extra spaces, convert to lowercase)
  const cleanWord = word?.trim().toLowerCase();

  // Check if we should show the lookup button
  const shouldShowLookup = cleanWord && cleanWord.length > 0;


  const fetchDefinition = async () => {
    if (!cleanWord) return;
    if (checksLeft <= 0) return;

    // Check cache first (cached lookups don't cost a check)
    if (cache[cleanWord]) {
      setDefinition(cache[cleanWord]);
      setIsModalOpen(true);
      return;
    }

    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    setLoading(true);
    setError(null);
    setIsModalOpen(true);

    try {
      const found = await lookupWord(cleanWord, { category });
      const definitionData = {
        word: found.word,
        phonetic: found.phonetic,
        partOfSpeech: found.partOfSpeech,
        definition: found.definition,
        example: found.example,
        synonyms: [],
        categoryMatch: found.categoryMatch,
        source: found.source,
      };

      // Cache the result
      setCache((prev) => ({
        ...prev,
        [cleanWord]: definitionData,
      }));

      setDefinition(definitionData);
      // Only charge a free lookup once a definition actually came back — a
      // failed request (or retrying after one) must not burn the allowance
      // with nothing to show for it.
      if (onCheckUsed) onCheckUsed();
    } catch (err) {
      console.error("Dictionary API error:", err);

      if (err.message === "not_found") {
        setError({
          type: "not_found",
          message:
            "We couldn't find this word anywhere. It might be slang or misspelled.",
        });
      } else {
        setError({
          type: "api_error",
          message: "Dictionary service unavailable. Try again later.",
        });
      }
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setError(null);
  };

  // Don't render if no word provided
  if (!shouldShowLookup) {
    return null;
  }

  return (
    <>
      {/* Define Button — always shows remaining checks */}
      <button
        onClick={fetchDefinition}
        disabled={(checksLeft <= 0 && !cache[cleanWord]) || loading}
        className={`flex items-center justify-center w-8 h-8 text-sm font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300 ${
          (checksLeft <= 0 && !cache[cleanWord]) || loading
            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
            : "bg-blue-600 hover:bg-blue-700 text-white"
        }`}
        title={checksLeft <= 0 && !cache[cleanWord] ? "No checks left" : `Define "${word}"`}
        aria-label={`Define ${word}`}
      >
        <svg
          className="w-4 h-4 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        {/* Remaining-lookups count hidden to keep answer cards compact; the limit still applies.
        {!cache[cleanWord] && (
          <span className="text-xs font-bold opacity-80">
            {checksLeft === Infinity ? "∞" : checksLeft}
          </span>
        )} */}
      </button>

      {/* Modal */}
      {isModalOpen && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Dictionary Lookup"
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto">
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                Dictionary Lookup
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold w-11 h-11 -mr-2 flex items-center justify-center rounded hover:bg-gray-100 touch-manipulation"
                aria-label="Close dictionary"
              >
                ×
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              {loading && (
                <NameySays mood="thinking" className="py-4">
                  <p className="text-gray-600">Hmm, let me look up "{word}"…</p>
                </NameySays>
              )}

              {error && (
                <div className="py-2">
                  <NameySays mood={error.type === "not_found" ? "dizzy" : "sad"} className="mb-4">
                    <h4 className="font-semibold text-gray-800 mb-1">
                      {error.type === "not_found"
                        ? "Word Not Found"
                        : "Service Error"}
                    </h4>
                    <p className="text-gray-600 text-sm">{error.message}</p>
                  </NameySays>

                  {error.type === "not_found" && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                      <strong>Tip:</strong> Check for typos, or the word might
                      be valid slang, a proper noun, or technical term not in
                      standard dictionaries.
                    </div>
                  )}
                </div>
              )}

              {definition && (
                <div className="space-y-4">
                  {/* Word and Pronunciation */}
                  <div className="text-center border-b border-gray-100 pb-4">
                    <h4 className="text-2xl font-bold text-gray-800 capitalize">
                      {definition.word}
                    </h4>
                    {definition.phonetic && (
                      <p className="text-gray-500 text-sm mt-1">
                        {definition.phonetic}
                      </p>
                    )}
                  </div>

                  <NameySays mood="happy">
                    {definition.partOfSpeech && (
                      <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-1 rounded uppercase mb-2">
                        {definition.partOfSpeech}
                      </span>
                    )}
                    <p className="text-gray-700 leading-relaxed">
                      {definition.definition}
                    </p>
                    {definition.source && definition.source !== "dictionary" && (
                      <p className="text-xs text-gray-400 mt-1">
                        Not in Merriam-Webster · definition from {definition.source === "ai" ? "AI" : "Wikipedia"}
                      </p>
                    )}
                  </NameySays>

                  {/* Example */}
                  {definition.example && (
                    <div>
                      <h5 className="font-semibold text-gray-800 mb-2">
                        Example:
                      </h5>
                      <p className="text-gray-600 italic bg-gray-50 p-3 rounded border-l-4 border-blue-300">
                        "{definition.example}"
                      </p>
                    </div>
                  )}

                  {/* Category Match Indicator */}
                  {definition.categoryMatch && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-center space-x-2">
                        <span className="text-green-600 text-lg">✅</span>
                        <div>
                          <span className="text-green-800 font-semibold">
                            Fits "{category}" category
                          </span>
                          {definition.categoryMatch.confidence === "medium" && (
                            <span className="text-green-600 text-sm ml-1">
                              (likely)
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Synonyms */}
                  {definition.synonyms && definition.synonyms.length > 0 && (
                    <div>
                      <h5 className="font-semibold text-gray-800 mb-2">
                        Synonyms:
                      </h5>
                      <div className="flex flex-wrap gap-2">
                        {definition.synonyms.map((synonym, index) => (
                          <span
                            key={index}
                            className="bg-green-100 text-green-800 text-sm px-2 py-1 rounded"
                          >
                            {synonym}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <div className="flex justify-between items-center">
                <p className="text-xs text-gray-500">
                  Powered by Merriam-Webster Dictionary
                </p>
                <button
                  onClick={closeModal}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 min-h-[44px] rounded text-sm font-medium transition-colors touch-manipulation"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default DictionaryLookup;
